import { FC, useCallback, useMemo } from 'react'
import { Button, Label } from '@cargomart/ui'
import cn from 'classnames'
import { PlaceholderCell } from 'components/extend/PlaceholderCell'
import { formatPrice } from 'helpers/common'
import { formatDateToMoscow } from 'helpers/dates'
import { TOrderPayment } from 'modules/order/types'
import {
  OrderItem,
  PaymentItem,
  PaymentTransactionCreateFromOrderRequest,
  PaymentTransactionCreateResponse
} from 'schema'
import { changeFields } from 'shared/forms/mutators'
import { handleFetchResponse } from 'src/api/errors'
import { compose, email, FieldFormItem, Form, trimRequired } from 'src/forms'
import { FieldText } from 'src/shared/forms'

import { useCreatePaymentOrderMutation } from '../../api'

import s from './OrderPaymentBlock.module.less'

const initialValues: PaymentTransactionCreateFromOrderRequest = {
  method: PaymentTransactionCreateFromOrderRequest.Method.CARD,
  email: ''
}

const paymentText = {
  [OrderItem.paymentStatus.NO]: 'Ожидается оплата',
  [OrderItem.paymentStatus.PART]: 'Оплачено частично',
  [OrderItem.paymentStatus.OVER]: 'Переплата',
  [OrderItem.paymentStatus.FULL]: 'Оплачено полностью'
}

type TProps = {
  isPaymentLoading: boolean
  payment: TOrderPayment[] | undefined
  paymentStatus: OrderItem.paymentStatus
  totalPaymentAmount: string
}

export const OrderPaymentBlock: FC<TProps> = ({ isPaymentLoading, paymentStatus, payment, totalPaymentAmount }) => (
  <div className="mb-4" data-test-id="order--payment--block">
    {isPaymentLoading ? (
      <>
        <PlaceholderCell height={20} />
        <div className={s.divider} />
        <PlaceholderCell height={20} />
        <PlaceholderCell height={20} />
        <PlaceholderCell height={20} />
      </>
    ) : (
      <DefaultPaymentLayout payment={payment} paymentStatus={paymentStatus} totalPaymentAmount={totalPaymentAmount} />
    )}
  </div>
)

const DefaultPaymentLayout: FC<Pick<TProps, 'payment' | 'totalPaymentAmount' | 'paymentStatus'>> = ({
  paymentStatus,
  payment,
  totalPaymentAmount
}) => {
  const paymentDiff = useMemo(() => {
    if (!payment || !payment?.length) {
      return 0
    }

    return (
      Number(totalPaymentAmount) -
      payment.reduce((acc, current) => {
        const sign = current.type === PaymentItem.type.INCOME ? 1 : -1

        return acc + sign * Number(current.amount)
      }, 0)
    )
  }, [payment, totalPaymentAmount])

  if (!payment || !payment.length) return null

  return (
    <>
      {payment.map((el, index) => (
        <div key={index} className={s.paymentRow}>
          <div className={s.paymentDate}>{formatDateToMoscow(el.date, true)}</div>
          <div className={cn(s.paymentAmount, el.type === PaymentItem.type.INCOME ? s.paymentAmount__success : '')}>
            {el.type === PaymentItem.type.INCOME ? '+' : '-'} {formatPrice(el.amount, true)} ₽
          </div>
        </div>
      ))}
      <div className={s.divider} />
      <div className={s.paymentStatus}>{paymentText[paymentStatus]}</div>
      {paymentDiff > 0 && <div className={s.paymentDiff}>К доплате: {formatPrice(paymentDiff, true)} ₽</div>}
      {paymentDiff < 0 && <div className={s.paymentDiff}>К возврату: {formatPrice(-paymentDiff, true)} ₽</div>}
    </>
  )
}

export const PaymentForm: FC<{ id: string }> = ({ id }) => {
  const [createPayment] = useCreatePaymentOrderMutation()

  const handleSuccess = useCallback((data: PaymentTransactionCreateResponse) => {
    const redirectUrl = data.data?.['payment-transaction'].formUrl

    if (!redirectUrl) return

    window.location.replace(redirectUrl)
  }, [])

  const handleSubmit = useCallback(
    (data: PaymentTransactionCreateFromOrderRequest) =>
      createPayment({ ...data, id }).then(handleFetchResponse(handleSuccess)),
    [createPayment, handleSuccess, id]
  )

  return (
    <Form<PaymentTransactionCreateFromOrderRequest>
      keepDirtyOnReinitialize
      initialValues={initialValues}
      mutators={{ changeFields }}
      name="createOrderYarcheForm"
      onSubmit={handleSubmit}
      render={(formProps) => (
        <form name="createOrderYarcheForm" onSubmit={formProps.handleSubmit}>
          <FieldFormItem
            className="w-full"
            help="На этот адрес отправим ваш чек об оплате"
            label={<Label>Электронная почта</Label>}
            name="email"
          >
            <FieldText
              className="w-full"
              name="email"
              placeholder="example@mail.ru"
              validate={compose(trimRequired, email)}
            />
          </FieldFormItem>
          <Button className="mt-4" size="lg" type="submit">
            Перейти к оплате
          </Button>
        </form>
      )}
    />
  )
}
